// Single source of truth for everything kept in localStorage.
// Every page reads settings through these helpers so defaults and
// migrations live in one place.

export const BUILTIN_LOGOS = [
  "toyota",
  "mitsubishi",
  "nissan",
  "honda",
  "mazda",
  "greatwall",
  "hyundai",
  "mercedes",
  "lexus",
  "bmw",
];

export const DEFAULT_BUSINESS = {
  name: "MILON MOTORS",
  taglines: [
    "Special For Toyota, Nissan, Mitsubishi, Honda, Mazda,",
    "Hyundai, BMW, Mercedes and Haval Great Wall",
  ],
  address: "87 New Road, Home Town AC Market, Bangla Motor, Dhaka 1000",
  services:
    "All Kinds Of Motors, Vehicle Parts, Importer, Wholesaler & Retail Supplier",
  phones: "",
  emails: "",
  logo: "", // data URL; empty = built-in Milon logo
  accentColor: "#3f81c3",
  footerLogos: BUILTIN_LOGOS.map((key) => ({ id: key, builtin: key })),
};

export const DEFAULT_SETTINGS = {
  defaultCurrency: "TK",
  taxation: "GST",
  taxationPer: 0,
  discounts: 0,
  shipping: 0,
  invoiceLabel: "Invoice No",
  autoDueDate: false,
  autoDueDays: 0,
  invoiceFrom: "",
  terms: "",
  footNote: "",
  units: ["pcs", "set", "pair", "ltr", "box"],
  paymentMethods: ["Cash", "bKash", "Nagad", "Bank", "Card"],
  addCustomerOnSave: true,
  paidLabel: "Paid",
  signatureLabel: "Signature",
  showTaxColumn: false,
  showReference: true,
  referenceLabel: "Vehicle No",
};

export const DEFAULT_DOC = {
  backgroundColor: "#296374",
  pageOrientation: "portrait",
  pageSize: "A4",
  watermark: { text: "", opacity: 0.3, bold: false, italics: false },
  qrCodeActive: false,
  qrCode: {
    qr: "",
    foreground: "#000000",
    background: "#FFFFFF",
    eccLevel: "L",
    appendInvoiceNo: false,
  },
};

const read = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const write = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (e) {
    // Most likely the 5MB quota; let the caller tell the user.
    console.error(`Could not save "${key}"`, e);
    return false;
  }
};

export const getBusiness = () => ({ ...DEFAULT_BUSINESS, ...(read("businessProfile") || {}) });
export const saveBusiness = (b) => {
  const ok = write("businessProfile", b);
  // Lets the navbar pick up name/logo changes without a reload.
  if (ok) window.dispatchEvent(new Event("mm:business"));
  return ok;
};

export const getSettings = () => {
  const stored = read("defaultSettings") || {};
  const merged = { ...DEFAULT_SETTINGS, ...stored };
  // Old versions kept the label text ("Invoice No:") in `invoiceNo`.
  if (!stored.invoiceLabel && typeof stored.invoiceNo === "string" && isNaN(Number(stored.invoiceNo))) {
    merged.invoiceLabel = stored.invoiceNo.replace(/:\s*$/, "");
  }
  if (!Array.isArray(merged.units) || merged.units.length === 0) merged.units = DEFAULT_SETTINGS.units;
  if (!Array.isArray(merged.paymentMethods) || merged.paymentMethods.length === 0) {
    merged.paymentMethods = DEFAULT_SETTINGS.paymentMethods;
  }
  return merged;
};
export const saveSettings = (s) => write("defaultSettings", s);

export const getDoc = () => {
  const stored = read("docDefinition") || {};
  return {
    ...DEFAULT_DOC,
    ...stored,
    watermark: { ...DEFAULT_DOC.watermark, ...(stored.watermark || {}) },
    qrCode: { ...DEFAULT_DOC.qrCode, ...(stored.qrCode || {}) },
  };
};
export const saveDoc = (d) => write("docDefinition", d);

export const getInvoices = () => {
  const list = read("invoices");
  return Array.isArray(list) ? list : [];
};
export const saveInvoices = (list) => write("invoices", list);

export const getTemplates = () => {
  const list = read("invoiceTemplates");
  return Array.isArray(list) ? list : [];
};
export const saveTemplates = (list) => write("invoiceTemplates", list);

const readList = (key) => {
  const list = read(key);
  return Array.isArray(list) ? list : [];
};

export const getCustomers = () => readList("customers");
export const saveCustomers = (list) => write("customers", list);

export const getProducts = () => readList("products");
export const saveProducts = (list) => write("products", list);
